import SailboatOffshore24 from '@carbon/icons-react/es/sailboat--offshore/24';
import TrashCan16 from '@carbon/icons-react/es/trash-can/16';
import { Tooltip, Typography } from 'antd';
import classNames from 'classnames';
import moment from 'moment';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useMutation, useQuery } from 'react-query';
import styled from 'styled-components';
import { TimeoffAPI } from 'api/timeoff';
import type { TimeoffResponse } from 'api/timeoff/types';
import { Button } from 'components/atoms/button';
import { Popconfirm } from 'components/atoms/popconfirm';
import { Table } from 'components/molecules/table';
import { toastService } from 'components/molecules/toast';
import { DrawerContent } from 'components/organisms/task-pane/task-detail';
import { DeleteButton } from 'components/organisms/timesheets/common/style';
import { useTranslate } from 'hooks/use-translation';
import { logger } from 'services';
import { callWorkerWithCleanup } from 'utils/call-worker-with-cleanup';
import { formatTimeOffDate } from './timeoff-date';
import { TimeOffDrawerContentStyled } from './timeoff.styles';

type TimeoffHistoryProps = {
  handleClose: () => void;
};

const StyledTable = styled(Table)`
  .ant-table-row {
    > td:last-child > svg {
      visibility: hidden;
    }

    &:hover {
      > td:last-child > svg {
        visibility: visible;
      }
    }
  }
`;

const RowWithToolTip = ({ children, ...restProps }) => {
  const translate = useTranslate();

  const tooltipText = translate('settings.profile.timeoff.deleted.tooltip');

  return (
    <Tooltip
      title={`${
        children[0]?.props.record.timeoffData?.deleted ? tooltipText : ''
      }`}
    >
      <tr {...restProps}>{children}</tr>
    </Tooltip>
  );
};

const getBusinessDatesCount = (startDate, endDate) => {
  let count = 0;
  let curDate = new Date(startDate);
  const lastDate = new Date(endDate);
  const day = 60 * 60 * 24 * 1000;
  const currentYear = new Date().getFullYear();

  // if there's no year, we get it as 2001, so we set it to current year
  if (curDate.getFullYear() === 2001) {
    curDate.setFullYear(currentYear);
  }

  if (lastDate.getFullYear() === 2001) {
    lastDate.setFullYear(currentYear);
  }

  while (curDate.getTime() < lastDate.getTime()) {
    if (curDate.getDay() !== 6 && curDate.getDay() !== 0) {
      count += 1;
    }
    curDate = new Date(curDate.getTime() + day);
  }

  return count;
};

export const TimeoffHistory: React.FC<TimeoffHistoryProps> = ({
  handleClose,
}) => {
  const translate = useTranslate();
  const timeoffDeleteSucessTitle = translate(
    'settings.profile.timeoff.delete.sucess.title',
  );
  const timeoffDeleteSucessDescription = translate(
    'settings.profile.timeoff.delete.sucess.description',
  );
  const timeoffDeleteErrorTitle = translate('failure');
  const timeoffDeleteErrorDescription = translate(
    'settings.profile.timeoff.delete.error.description',
  );

  // mutation to delete timeoff
  const deleteTimeoffMutation = useMutation<
    TimeoffResponse,
    { code: string; reason: string },
    number
  >((timeoffId) => TimeoffAPI.deleteTimeoff(timeoffId), {
    onSuccess: (timeoffResponse) => {
      toastService({
        title: timeoffDeleteSucessTitle,
        message: timeoffDeleteSucessDescription,
      }).success();

      const worker = callWorkerWithCleanup();

      if (timeoffResponse?.userId) {
        worker.workerApi
          .deleteUserTimeoff({
            timeoffId: timeoffResponse.timeOffId,
            userId: timeoffResponse.userId,
          })
          .catch((err) => {
            logger.error(
              `Error occured while deleting timeoff ${timeoffResponse.timeOffId}`,
              err,
            );
          });
      }

      handleClose();
    },
    onError: () => {
      toastService({
        title: timeoffDeleteErrorTitle,
        message: timeoffDeleteErrorDescription,
      }).error();
    },
  });

  const columns = [
    {
      title: (
        <FormattedMessage id="settings.profile.timeoff.drawer.history.table.header.timeoff_date" />
      ),
      dataIndex: 'timeoffData',
      key: 'timeoffData',
      render: ({
        startDate,
        endDate,
        note,
        deleted,
      }: {
        startDate: string;
        endDate: string;
        note: string;
        deleted: boolean;
      }) => {
        const dateDiff = getBusinessDatesCount(startDate, endDate) + 1;

        const dateDiffPluralized = dateDiff === 1 ? 'day' : 'days';

        const classList = classNames('timeoff-table-column-timeoff-date', {
          'timeoff-deleted': deleted,
        });

        return (
          <div className={classList}>
            <div>
              <span className="timeoff-history-datestring">
                {startDate === endDate
                  ? startDate
                  : `${startDate} - ${endDate}`}
              </span>

              <span className="timeoff-history-datediff">
                ({dateDiff} {dateDiffPluralized})
              </span>
            </div>
            {note?.length > 60 ? (
              <Tooltip placement="topLeft" title={note}>
                <div className="timeoff-note">{note}</div>
              </Tooltip>
            ) : (
              <div className="timeoff-note">{note?.length ? note : '——'}</div>
            )}
          </div>
        );
      },
    },
    {
      title: (
        <FormattedMessage id="settings.profile.timeoff.drawer.history.table.header.applied_on" />
      ),
      dataIndex: 'appliedOn',
      key: 'appliedOn',
      width: '150px',
    },
    {
      title: '',
      key: 'action',
      width: '50px',
      render: (data) => {
        const { timeoffData, id } = data;

        if (!timeoffData.deleted) {
          return (
            <Popconfirm
              placement="leftTop"
              getPopupContainer={(trigger) =>
                trigger.parentElement as HTMLElement
              }
              subTitle={
                timeoffData.startDate === timeoffData.endDate ? (
                  <FormattedMessage
                    id="settings.profile.timeoff.drawer.history.single.delete.popup.description"
                    values={{
                      strong: (chunks) => (
                        <Typography.Text strong>{chunks}</Typography.Text>
                      ),
                      startDate: timeoffData.startDate,
                    }}
                  />
                ) : (
                  <FormattedMessage
                    id="settings.profile.timeoff.drawer.history.range.delete.popup.description"
                    values={{
                      strong: (chunks) => (
                        <Typography.Text strong>{chunks}</Typography.Text>
                      ),
                      startDate: timeoffData.startDate,
                      endDate: timeoffData.endDate,
                    }}
                  />
                )
              }
              onConfirm={() => deleteTimeoffMutation.mutate(id)}
            >
              <Tooltip
                placement="bottomRight"
                title={
                  <FormattedMessage id="settings.profile.timeoff.delete.tooltip" />
                }
              >
                <DeleteButton icon={<TrashCan16 />} />
              </Tooltip>
            </Popconfirm>
          );
        }

        return null;
      },
    },
  ];

  const components = {
    body: {
      row: RowWithToolTip,
    },
  };

  const handleCancel = () => {
    handleClose();
  };

  const { data, isLoading } = useQuery(
    'timeoff-history',
    async () => {
      return TimeoffAPI.getTimeoffHistory();
    },
    {
      cacheTime: 1000,
    },
  );

  const timeoffData = data?.length
    ? data
        .map((item) => {
          return {
            key: item.timeOffId,
            timeoffData: {
              startDate: formatTimeOffDate(item.timeOffStart),
              endDate: formatTimeOffDate(item.timeOffEnd),
              note: item.note,
              deleted: item.deleted,
            },
            appliedOn: formatTimeOffDate(item.createdAt),
            id: item.timeOffId,
          };
        })
        .sort((a, b) => {
          return moment
            .utc(b.timeoffData.startDate)
            .diff(moment.utc(a.timeoffData.startDate));
        })
    : [];

  return (
    <TimeOffDrawerContentStyled width="600px">
      <DrawerContent.Title data-cy="settings.profile.timeoff.drawer.history.title">
        <div className="timeoff-history-header">
          <SailboatOffshore24 />
          <FormattedMessage
            tagName="span"
            id="settings.profile.timeoff.drawer.history.title"
          />
        </div>
      </DrawerContent.Title>

      <DrawerContent.Content>
        <StyledTable
          loading={isLoading}
          pagination={false}
          dataSource={timeoffData}
          columns={columns}
          components={components}
        />
      </DrawerContent.Content>

      <DrawerContent.Footer className="timeoff-history-footer">
        <Button
          type="primary"
          onClick={handleCancel}
          data-cy="profile.timeoff-history-drawer.done"
        >
          <FormattedMessage id="done" />
        </Button>
      </DrawerContent.Footer>
    </TimeOffDrawerContentStyled>
  );
};
